import { LoadingButton } from '@mui/lab';
import { Tab, Tabs, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import React, { FormEvent, useMemo, useState } from 'react';
import { BannersDTO } from '../../types';
import styles from './styles.module.css';
import { ACTIVE_TAB } from '../../../BlogPage/components/PostForm/consts';
import { chooseLangAccordingToTab } from '../../../ServicesPage/components/useServiceInfoForm';
import { useAlert } from '../../../../context/Alert.context';

interface PageFormProps {
  onSubmit: (data: { page: BannersDTO; file: File | null }) => void;

  onCancel: () => void;
  isEdit?: boolean;
  defaultValue?: BannersDTO;
  loading: boolean;
}

export const BannerForm = ({
  onSubmit,
  defaultValue,
  onCancel,
  loading,
  isEdit
}: PageFormProps) => {
  const [page, setPage] = useState(
    defaultValue || {
      titleEn: '',
      titleRu: '',
      titlePl: '',
      titleUk: '',

      subTitleEn: '',
      subTitleRu: '',
      subTitlePl: '',
      subTitleUk: '',

      btnTextRu: '',
      btnTextPl: '',
      btnTextUk: '',
      btnTextEn: '',

      link: '',
    },
  );
  const [file, setFile] = useState<File | null>(null);

  const [preview, setPreview] = useState<string | null>(
    defaultValue?.image ?? null,
  );
  const [activeTabIndex, setActiveTabIndex] = useState(ACTIVE_TAB.POLISH);
  const { showAlert } = useAlert();
  const title = `title${chooseLangAccordingToTab(activeTabIndex)}` as 'titleEn' | 'titleRu' | 'titleUk' | 'titlePl';
  const btnText = `btnText${chooseLangAccordingToTab(activeTabIndex)}` as 'btnTextEn' | 'btnTextRu' | 'btnTextUk' | 'btnTextPl';
  const subTitle = `subTitle${chooseLangAccordingToTab(activeTabIndex)}` as 'subTitleEn' | 'subTitleRu' | 'subTitleUk' | 'subTitlePl';

  const onSubmitHdl = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitBtnDisabled) return;

    onSubmit({
      page: page as BannersDTO,
      file,
    });
  };

  const isSubmitBtnDisabled = useMemo(() => {
    return false;
  }, [page, defaultValue]);

  const onTabClick = (_: React.SyntheticEvent, nextIndex: number) => {
    setActiveTabIndex(nextIndex);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith('image/')) {
      showAlert({ message: 'Please upload image', severity: 'error' });
      return;
    }

    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const isDisabled = useMemo(() => {
    return !page.titleEn?.trim() || !page.titleUk?.trim() || !page.titleRu?.trim() || !page.titlePl?.trim() || (isEdit ? !preview : !file );
  }, [page.titleEn, page.titleUk, page.titleRu, page.titlePl, file,preview])

  return (
    <form className={styles.form} onSubmit={onSubmitHdl} noValidate>
      <div className={styles.imageUpload}>
        <Typography>Banner image</Typography>
        <div className={styles.imageUploadContent}>
          {preview && (
            <img
              src={preview}
              alt="Banner preview"
              className={styles.preview}
            />
          )}

          <Button
            variant="outlined"
            component="label"
          >
            Choose file
            <input
              type="file"
              hidden
              required
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>

          {file && (
            <Typography variant="caption" color="text.secondary">
              {file.name}
            </Typography>
          )}
        </div>
      </div>
      <TextField
        name="link"
        label="Link"
        value={page.link}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            link: e.target.value,
          }))
        }
      />
      <div className={styles.tabContainer}>
        <Tabs value={activeTabIndex} onChange={onTabClick}>
          <Tab label={<TabLabel label="Polish"  />} />
          <Tab
            label={<TabLabel label="English"  />}
          />
          <Tab
            label={<TabLabel label="Russian"  />}
          />
          <Tab
            label={<TabLabel label="Ukranian"  />}
          />
        </Tabs>
      </div>

      <TextField
        name="title"
        label="Title"
        value={page[title]}
        variant="standard"
        required
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [title]: e.target.value,
          }))
        }
      />
      <TextField
        name="subTitle"
        label="Subtitle"
        value={page[subTitle]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [subTitle]: e.target.value,
          }))
        }
      />
      <TextField
        name="btnText"
        label="Button text"
        value={page[btnText]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [btnText]: e.target.value,
          }))
        }
      />

      <DialogActions>
        <Button disabled={loading} variant="outlined" onClick={onCancel}>
          CANCEL
        </Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="contained"
          disabled={isSubmitBtnDisabled || isDisabled}
        >
          SAVE
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

const TabLabel = ({ label }: { label: string }) => {
  return (
    <div className={styles.tabItem}>
      <Typography variant="body1" >
        {label}
      </Typography>
    </div>
  );
};
